import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, distinctUntilChanged, Observable, of, Subject } from 'rxjs';
import { catchError, share, shareReplay, takeUntil, tap } from 'rxjs/operators';
import { User } from '@src/app/_models/user/user';
import { registerLocaleData } from '@angular/common';
import moment from 'moment';
import localeCs from '@angular/common/locales/cs';
import { EnvironmentService } from '@src/app/_services/environment.service';

moment.updateLocale('cs', { workingWeekdays: [1, 2, 3, 4, 5] });
registerLocaleData(localeCs);

@Injectable({
    providedIn: 'root',
})
export class DataService {
    private readonly usersUrl: string;

    private readonly resultClientServiceSource = new BehaviorSubject<unknown[]>([] as unknown[]);

    private readonly resultUserSource = new BehaviorSubject<User[] | undefined>([]);

    private readonly resultUserDetailSource = new BehaviorSubject<User | null | undefined>(null);

    private readonly destroy$ = new Subject<void>();

    userSource = this.resultUserSource.asObservable().pipe(distinctUntilChanged());

    userDetailSource = this.resultUserDetailSource.asObservable().pipe(distinctUntilChanged());

    // eslint-disable-next-line max-lines-per-function
    constructor(
        private readonly http: HttpClient,
        private readonly environmentService: EnvironmentService
    ) {
        this.usersUrl = `${this.environmentService.backendURL}/api/user`;
    }

    /**
     * Handle Http operation that failed.
     * Let the app continue.
     * @param operation - name of the operation that failed
     * @param result? - optional value to return as the observable result
     */
    private handleError<T>(operation = 'operation', result?: T) {
        return (error: { [key: string]: string }): Observable<T | undefined> => {
            // TODO: send the error to remote logging infrastructure
            console.error(error); // log to console instead
            // TODO: better job of transforming error for user consumption
            DataService.log(`${operation} failed: ${error.message}`);

            // eslint-disable-next-line @typescript-eslint/no-unsafe-return
            return of(result);
        };
    }

    /** Log a ticketService message with the MessageService */
    private static log(message: string): void {
        console.info(message);
    }

    // USERS GETTERS
    private getUsersData(): Observable<User[] | undefined> {
        return this.http.get<User[]>(this.usersUrl).pipe(
            tap(() => {
                DataService.log('fetched users');
            }),
            shareReplay(),
            catchError(this.handleError<User[]>('getUsersQueue', []))
        );
    }

    private getUserData(idUser: number): Observable<User | undefined> {
        const url = `${this.usersUrl}/search/${idUser}`;

        return this.http.get<User>(url).pipe(
            tap(() => {
                DataService.log(`fetched users id=${idUser}`);
            }),
            share(),
            catchError(this.handleError<User>(`getUser id=${idUser}`))
        );
    }

    // USERS SETTERS
    setUsersDataSource(active?: boolean): void {
        if (this.resultUserSource.getValue()?.length === 0) {
            const resultUserData: User[] = [];

            this.getUsersData()
                .pipe(takeUntil(this.destroy$))
                .subscribe((users: User[]) => {
                    if (typeof users !== 'undefined' && users.length > 0) {
                        // eslint-disable-next-line complexity
                        users.forEach((user: User) => {
                            user.unixCreatedTime = moment(user.created_date).valueOf();
                            user.unixUpdatedTime = user.updated_date
                                ? moment(user.updated_date).valueOf()
                                : null;

                            user.unixDeletedTime = user.deleted_date
                                ? moment(user.deleted_date).valueOf()
                                : null;

                            if (active && user.authorized && !user.deleted_date) {
                                resultUserData.push(user);
                            } else {
                                resultUserData.push(user);
                            }
                        });
                    }

                    this.resultUserSource.next(resultUserData);
                });
        }
    }

    // eslint-disable-next-line max-lines-per-function
    setUserDetailSource(userData: User | number): void {
        const id = typeof userData === 'number' ? userData : userData.id;

        // eslint-disable-next-line max-lines-per-function
        this.getUserData(id)
            .pipe(takeUntil(this.destroy$))
            .subscribe((user: User) => {
                user.unixCreatedTime = moment(user.created_date).valueOf();
                user.unixUpdatedTime = user.updated_date
                    ? moment(user.updated_date).valueOf()
                    : null;
                user.unixDeletedTime = user.deleted_date
                    ? moment(user.deleted_date).valueOf()
                    : null;
                this.resultUserDetailSource.next(user);
            });
    }

    // CLEAR CACHES
    clearUserCache(): void {
        this.resultUserSource.next([]);
    }

    clearAllCache(): void {
        this.resultUserSource.next([]);
        this.resultClientServiceSource.next([]);
    }

    /**
     * Completes all subscriptions and cleans up resources.
     * Call this method when the service is no longer needed.
     */
    destroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
